import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Calendar } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { adminApi } from "../../api/admin.api.js";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { ImageThumbnail } from "../../components/upload/ImageUpload.jsx";
import { formatDate, formatRelativeTime } from "../../lib/format.js";

const STATUS_TONE = { draft: "default", scheduled: "warning", published: "success" };

export default function BlogsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "Blog Posts · Admin";
  }, []);

  const { list, remove } = useResource("blogs", { toastLabel: "blog post" });
  const { data, isLoading } = list({
    page,
    limit: 12,
    sort: "publishedAt:desc",
    ...(search && { search }),
    ...(category && { category }),
    ...(status && { status }),
  });

  const categoriesQuery = useQuery({
    queryKey: ["blog-categories", "list-for-filter"],
    queryFn: () => adminApi.list("blog-categories", { limit: 50 }),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    setPage(1);
  }, [search, category, status]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Content</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">Blog Posts</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} post{data?.meta?.total === 1 ? "" : "s"} total
          </p>
        </div>
        <Link to="/blogs/new">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New post</Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search posts by title…"
          filters={
            <>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="min-w-[180px]"
              >
                <option value="">All categories</option>
                {categoriesQuery.data?.items?.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="min-w-[140px]"
              >
                <option value="">Any status</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </Select>
            </>
          }
        />

        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          onRowClick={(row) => navigate(`/blogs/${row._id}`)}
          columns={[
            {
              key: "title",
              label: "Post",
              render: (r) => (
                <div className="flex items-center gap-3">
                  <ImageThumbnail src={r.heroImage} alt={r.title} />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text-primary">{r.title}</p>
                    <p className="truncate text-xs text-text-muted">
                      by {r.author || "—"} · {r.readingTime} min read
                    </p>
                  </div>
                </div>
              ),
            },
            {
              key: "category",
              label: "Category",
              render: (r) => <span className="text-text-secondary">{r.category?.name ?? "—"}</span>,
            },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <div className="flex items-center gap-1.5">
                  <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>
                  {r.isFeatured && <Badge tone="accent">featured</Badge>}
                </div>
              ),
            },
            {
              key: "publishedAt",
              label: "Published",
              render: (r) =>
                r.publishedAt ? (
                  <span className="flex items-center gap-1.5 text-text-secondary">
                    {r.status === "scheduled" && <Calendar className="h-3.5 w-3.5" />}
                    {formatDate(r.publishedAt)}
                    <span className="text-xs text-text-muted">
                      ({formatRelativeTime(r.publishedAt)})
                    </span>
                  </span>
                ) : (
                  <span className="text-text-muted">—</span>
                ),
            },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => navigate(`/blogs/${row._id}`)}
              onDelete={() => setConfirmDelete(row)}
            />
          )}
          empty={{
            title: "No blog posts yet",
            description: "Create your first post to start populating the public blog.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.title}"?`}
        description="The post will be removed from the public blog immediately."
        confirmLabel="Delete post"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}
